import * as React from "react";
import { Globe, ImageIcon } from "lucide-react";
import { Card, Skeleton } from "@/components/ui";
import { HoloBadge, HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { AspectRatio, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { useDesignPackageProgress } from "@/utils/actioncable";
import { ASPECT_RATIO_LABELS, CONTEXT_TYPE_LABELS, FORMAT_LABELS, isInFlightStatus } from "../packageStatus";
import { StatusPill } from "./StatusPill";

export type PackageCardVariant = "published" | "draft" | "global";

export interface PackageCardProps {
  pkg: DesignPackageSummary;
  variant: PackageCardVariant;
  onClick(): void;
  dataSkeleton?: boolean;
  className?: string;
  /** The Global badge on global cards; coaches browsing SocialCoach's designs don't need it. */
  showGlobalBadge?: boolean;
}

export const ASPECT_CLASS: Record<AspectRatio, string> = {
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
};

export function packageMetaLine(pkg: Pick<DesignPackageSummary, "isCarousel" | "aspectRatio">): string {
  return `${pkg.isCarousel ? FORMAT_LABELS.carousel : FORMAT_LABELS.single} · ${ASPECT_RATIO_LABELS[pkg.aspectRatio]}`;
}

export function cardVariantFor(pkg: DesignPackageSummary, isGlobalScope: boolean): PackageCardVariant {
  if (isGlobalScope) return "global";
  return pkg.publishedAt && !pkg.archived ? "published" : "draft";
}

/** Stand-in for skeleton cards, which render no package data. */
export const SKELETON_PACKAGE: DesignPackageSummary = {
  id: 0,
  name: "",
  aspectRatio: "square_1_1",
  contextType: "none",
  isCarousel: false,
  slidesCount: 0,
  slides: [],
  reviewSlots: 0,
  minReviews: 0,
  maxReviews: 0,
  isGlobal: false,
  coachId: null,
  status: "completed",
  progressStatus: null,
  percent: null,
  thumbnailUrl: null,
  publishedAt: null,
  archived: false,
  promptId: null,
  scheduledPostsCount: 0,
  backgroundPolicy: "suggested",
  pickerBackgrounds: [],
  postsCount: 0,
  promptSettings: null,
  createdAt: "",
  updatedAt: "",
  createdBy: null,
};

const PackageThumbnail: React.FC<{ pkg: DesignPackageSummary }> = ({ pkg }) => {
  const progress = useDesignPackageProgress(isInFlightStatus(pkg.status) ? pkg.id : null);
  const aspect = ASPECT_CLASS[pkg.aspectRatio];

  if (isInFlightStatus(pkg.status)) {
    const percent = progress?.percent ?? pkg.percent;
    return (
      <div className="max-h-56 overflow-hidden">
        <HoloShimmer
          aspectRatio={pkg.aspectRatio}
          label="Generating"
          sublabel={percent != null ? `${Math.round(percent)}%` : undefined}
        />
      </div>
    );
  }

  if (pkg.thumbnailUrl) {
    return <img src={pkg.thumbnailUrl} alt={pkg.name} className={cn("w-full max-h-56 object-cover", aspect)} />;
  }

  return (
    <div className={cn("w-full max-h-56 flex items-center justify-center bg-slate-100 text-slate-300", aspect)}>
      <ImageIcon className="size-8" aria-hidden="true" />
    </div>
  );
};

export const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  variant,
  onClick,
  dataSkeleton,
  className,
  showGlobalBadge = true,
}) => {
  if (dataSkeleton) {
    return (
      <Card
        data-testid="package-card-skeleton"
        className={cn("rounded-2xl border-slate-200 shadow-sm gap-0 py-0", className)}
      >
        <Skeleton className="aspect-square max-h-56 w-full rounded-b-none" />
        <div className="p-3 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      role="button"
      tabIndex={0}
      data-variant-kind={variant}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "relative rounded-2xl shadow-sm hover:shadow-md cursor-pointer overflow-hidden gap-0 py-0 transition-shadow",
        variant === "draft" ? "border border-dashed border-slate-300 bg-slate-50/40" : "border border-slate-200",
        className
      )}
    >
      {variant === "global" && showGlobalBadge && (
        <HoloBadge label="Global" icon={Globe} className="absolute top-2 left-2 z-10" />
      )}
      <PackageThumbnail pkg={pkg} />
      <div className="p-3 flex flex-col gap-1 min-w-0">
        <span className="font-bold text-slate-800 truncate">{pkg.name}</span>
        <span className="text-xs text-slate-500">{packageMetaLine(pkg)}</span>
        <span className="text-xs text-slate-500">{CONTEXT_TYPE_LABELS[pkg.contextType]}</span>
        {variant !== "global" && (
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <StatusPill pkg={pkg} />
            {variant === "published" && <span className="text-xs text-slate-500">{pkg.postsCount} posts</span>}
          </div>
        )}
        {variant === "draft" && <span className="text-[11px] text-slate-400">Not visible to players yet</span>}
      </div>
    </Card>
  );
};
